'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import * as prismicH from '@prismicio/helpers';

import { VideoSlice, VideoSliceDefault } from '@/types';
import { VideoSliceHeroVideo } from 'prismicio-types';
import { useSelectedStore, useShowByLocationSlug } from '@/hooks';

import DefaultVariation from './DefaultVariation';
import HeroVideoVariation from './HeroVideoVariation';

type VideoProps = VideoSlice & {
  aspectRatio: string;
  initialStoreUrlSlug?: string;
  /** Authoritative on SSR/first paint; client store picker takes over after mount. */
  isServerVisible: boolean;
};

const Video: FC<VideoProps> = ({
  aspectRatio,
  isServerVisible,
  initialStoreUrlSlug,
  ...slice
}) => {
  const { store } = useSelectedStore();
  const { showContent } = useShowByLocationSlug(slice.primary?.location_slug);
  const [isMounted, setIsMounted] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(isServerVisible);
  const selectedSlug = store?.url_slug;
  const videoUrl = prismicH.asLink(slice.primary?.video_content) || 'no-video';

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Trust SSR after refresh when the selected store matches the server cookie.
  const visible = useMemo(() => {
    if (!isMounted) {
      return isServerVisible;
    }

    if (!selectedSlug || selectedSlug === initialStoreUrlSlug) {
      return isServerVisible;
    }

    return showContent;
  }, [
    isMounted,
    isServerVisible,
    initialStoreUrlSlug,
    selectedSlug,
    showContent,
  ]);

  useEffect(() => {
    if (visible) {
      setHasBeenVisible(true);
    }
  }, [visible]);

  const keepMounted = visible || hasBeenVisible;

  if (!keepMounted) {
    return null;
  }

  if (slice.variation === 'heroVideo') {
    return (
      <HeroVideoVariation
        aspectRatio={aspectRatio}
        isStoreVisible={visible}
        key={videoUrl}
        slice={slice as VideoSlice & VideoSliceHeroVideo}
      />
    );
  }

  return (
    <DefaultVariation
      aspectRatio={aspectRatio}
      isStoreVisible={visible}
      key={videoUrl}
      slice={slice as VideoSlice & VideoSliceDefault}
    />
  );
};

export default Video;
